<template>
  <div id="competitors-table-for-print" class="competitor-list-section" :class="{ 'has-competitors': competitors.length > 0 }">
    <div class="competitor-list-header" :class="{ 'is-compact': !showTitle }">
      <!-- Always in the DOM: printing clones this node into a new window, where
           the scoped `screen-hidden` rule does not exist, so it shows on paper -->
      <div class="competitor-list-heading" :class="{ 'screen-hidden': !showTitle }">
        <div v-if="tournamentName" class="competitor-list-tournament">{{ tournamentName }}</div>
        <h2 class="competitor-list-title">{{ title }}</h2>
        <div v-if="genderText" class="competitor-list-subtitle">
          <span class="gender-text">{{ genderText }}</span>
        </div>
      </div>
      <div class="competitor-count">Ukupno takmičara: {{ competitors.length }}</div>
    </div>
    <table class="competitor-table">
      <thead>
        <tr>
          <th class="col-ordinal">Rb.</th>
          <th v-if="!isTeam && showCompetitorImage" class="col-image">Slika</th>
          <th class="col-name">Ime</th>
          <th v-if="!isTeam" class="col-belt">Pojas</th>
          <th v-if="!isTeam" class="col-date">Datum rođenja</th>
          <th class="col-club">Skraćeni Naziv Sportske Organizacije</th>
          <th class="col-placement">Plasman</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="competitors.length === 0">
          <td :colspan="tableColumnCount" class="empty-message">Nema prijavljenih takmičara</td>
        </tr>
        <tr v-for="(competitor, index) in competitors" :key="competitor._id || competitor.id">
          <td class="ordinal-cell">{{ index + 1 }}.</td>
          <td v-if="!isTeam && showCompetitorImage" class="image-cell">
            <div class="avatar-container">
              <div class="avatar-placeholder"></div>
              <img
                v-if="competitor.competitor && competitor.competitor.avatar"
                :src="$file('avatars', competitor.competitor.avatar)"
                class="competitor-avatar"
                alt=""
                @error="handleImageError"
              />
            </div>
          </td>
          <td class="name-cell">{{ getCompetitorDisplayNameWithGender(competitor) }}</td>
          <td v-if="!isTeam" class="belt-cell">
            {{ getBeltDisplayValue(competitor) }}
          </td>
          <td v-if="!isTeam" class="date-cell">
            {{ competitor.competitor && competitor.competitor.born ? formatDateFullMonth(competitor.competitor.born) : '-' }}
          </td>
          <td class="club-cell">
            <span v-if="showClubLogo && competitor.club" class="club-logo-wrapper">
              <span
                v-if="!competitor.club.logo || clubLogoFailed[getCompetitorKey(competitor)]"
                class="club-logo-placeholder"
                aria-hidden="true"
              />
              <img
                v-if="competitor.club.logo && !clubLogoFailed[getCompetitorKey(competitor)]"
                :src="$file('avatars', competitor.club.logo)"
                class="club-logo"
                alt=""
                @error="handleClubLogoError(competitor)"
              />
            </span>
            <span v-if="competitor.club" class="club-text">
              {{ competitor.club.shortName }}, {{ competitor.club.city }}
            </span>
          </td>
          <td class="placement-cell">
            <span class="placement-checkbox"></span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'BracketCompetitorList',

  data () {
    return {
      clubLogoFailed: {}
    }
  },

  props: {
    title: {
      type: String,
      required: true
    },
    // Hidden on screen where the bracket header already shows this info;
    // still rendered (and visible) in print, where the table stands on its own page
    showTitle: {
      type: Boolean,
      default: true
    },
    tournamentName: {
      type: String,
      default: ''
    },
    categoryName: {
      type: String,
      default: ''
    },
    genderText: {
      type: String,
      default: ''
    },
    competitors: {
      type: Array,
      default: () => []
    },
    isTeam: {
      type: Boolean,
      default: false
    },
    showCompetitorImage: {
      type: Boolean,
      default: true
    },
    showClubLogo: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    tableColumnCount () {
      if (this.isTeam) return 4
      return this.showCompetitorImage ? 7 : 6
    }
  },

  methods: {
    getCompetitorDisplayName (competitor) {
      if (!competitor) return 'Unknown'

      // Handle team competitors
      if ((competitor.isTeam || (competitor.teamMembers && competitor.teamMembers.length > 0)) && competitor.teamMembers && competitor.teamMembers.length > 0) {
        return competitor.teamMembers.map(member => {
          const name = `${member.firstName || ''} ${member.lastName || ''}`.trim()
          const gender = member.isMale ? 'M' : 'Ž'
          return `${name} (${gender})`
        }).join(', ')
      }

      // Handle individual competitors
      if (competitor.competitor) {
        const firstName = competitor.competitor.firstName || ''
        const lastName = competitor.competitor.lastName || ''
        return `${firstName} ${lastName}`.trim()
      }

      return 'Unknown'
    },

    getCompetitorDisplayNameWithGender (competitor) {
      if (!competitor) return 'Unknown'

      // Handle team competitors
      if ((competitor.isTeam || (competitor.teamMembers && competitor.teamMembers.length > 0)) && competitor.teamMembers && competitor.teamMembers.length > 0) {
        return competitor.teamMembers.map(member => {
          const name = `${member.firstName || ''} ${member.lastName || ''}`.trim()
          const gender = member.isMale ? 'M' : 'Ž'
          return `${name} (${gender})`
        }).join(', ')
      }

      // Handle individual competitors - add gender
      if (competitor.competitor) {
        const firstName = competitor.competitor.firstName || ''
        const lastName = competitor.competitor.lastName || ''
        const name = `${firstName} ${lastName}`.trim()
        // Try to get gender from competitor, otherwise use genderText prop
        let gender = ''
        if (competitor.competitor.isMale !== undefined) {
          gender = competitor.competitor.isMale ? 'M' : 'Ž'
        } else if (competitor.competitor.isFemale !== undefined) {
          gender = competitor.competitor.isFemale ? 'Ž' : 'M'
        } else if (this.genderText) {
          // Use genderText prop if available (from bracket)
          gender = this.genderText === 'Muški' ? 'M' : (this.genderText === 'Ženski' ? 'Ž' : '')
        }
        return gender ? `${name} (${gender})` : name
      }

      return 'Unknown'
    },

    getBeltDisplayValue (competitor) {
      if (!competitor) {
        return '-'
      }

      // Try to get belt from competitor.competitor.belt (standard structure)
      let belt = null
      if (competitor.competitor && competitor.competitor.belt !== undefined && competitor.competitor.belt !== null) {
        belt = competitor.competitor.belt
      } else if (competitor.belt !== undefined && competitor.belt !== null) {
        // Fallback: try competitor.belt directly
        belt = competitor.belt
      }

      if (belt === null || belt === undefined || belt === '') {
        return '-'
      }

      return this.getBeltName(belt)
    },

    getBeltName (belt) {
      if (belt === null || belt === undefined || belt === '') {
        return '-'
      }

      // Handle numeric belt values (0-6)
      const numericBeltMap = {
        0: 'Beli',
        1: 'Žuti',
        2: 'Narandžasti',
        3: 'Zeleni',
        4: 'Plavi',
        5: 'Braon',
        6: 'Crni'
      }

      if (typeof belt === 'number' || (typeof belt === 'string' && /^\d+$/.test(belt))) {
        const numericBelt = typeof belt === 'number' ? belt : parseInt(belt, 10)
        if (numericBeltMap[numericBelt] !== undefined) {
          return numericBeltMap[numericBelt]
        }
      }

      // Handle string belt values (like "CRNI", "BRAON", etc.) using the belt filter
      // Import belts from definitions to handle string keys
      try {
        const { belts } = require('@/common/definitions')
        if (belts[belt]) {
          return belts[belt]
        }
      } catch (e) {
        // Fallback if import fails
      }

      // If belt is already a display name string, return it as-is
      if (typeof belt === 'string') {
        const knownBelts = ['Beli', 'Žuti', 'Narandžasti', 'Zeleni', 'Plavi', 'Braon', 'Crni', 'Crveni', 'Ljubičasti', 'Oranž']
        if (knownBelts.includes(belt)) {
          return belt
        }
      }

      return '-'
    },

    formatDate (dateString) {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        return `${day}.${month}.${year}.`
      } catch (e) {
        return dateString
      }
    },

    formatDateFullMonth (dateString) {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)
        const day = date.getDate()
        const monthNames = [
          'januar', 'februar', 'mart', 'april', 'maj', 'jun',
          'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'
        ]
        const month = monthNames[date.getMonth()]
        const year = date.getFullYear()
        return `${day}. ${month} ${year}.`
      } catch (e) {
        return dateString
      }
    },

    handleImageError (event) {
      // Hide broken image, placeholder will show instead
      if (event.target) {
        event.target.style.display = 'none'
      }
    },

    getCompetitorKey (competitor) {
      return competitor._id || competitor.id || ''
    },

    handleClubLogoError (competitor) {
      const key = this.getCompetitorKey(competitor)
      if (key !== undefined && key !== '') {
        this.$set(this.clubLogoFailed, key, true)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.competitor-list-section {
  page-break-before: auto;
  page-break-inside: avoid;
  padding: 20px 0;
  min-height: 100px;
  display: block;
  visibility: visible;
}

.competitor-list-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #333;
}

/* Screen-only: the bracket header above already shows tournament/discipline/category.
   Scoped, so the print window (which gets a plain clone) still renders it. */
.competitor-list-heading.screen-hidden {
  display: none;
}

.competitor-list-header.is-compact {
  margin-bottom: 12px;
  padding-bottom: 0;
  border-bottom: none;
}

.competitor-list-tournament {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: #222;
  word-break: break-word;
}

.competitor-list-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  line-height: 1.4;
}

.competitor-list-subtitle {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.gender-text {
  color: #409eff;
}

.competitor-count {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.competitor-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;
}

.competitor-table thead {
  background-color: #f5f7fa;
}

.competitor-table th {
  padding: 10px 8px;
  text-align: left;
  font-weight: 600;
  border: none;
  border-bottom: 1px solid #333;
  color: #333;
  background-color: #f5f7fa;
}

.competitor-table td {
  padding: 8px 8px;
  border: none;
  border-bottom: 1px solid #333;
  vertical-align: middle;
  background-color: #fff;
}

.competitor-table tbody tr {
  height: auto;
  min-height: 80px;
}

.competitor-table tbody tr td {
  vertical-align: middle;
}

  .competitor-table th.col-ordinal,
  .competitor-table td.ordinal-cell {
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
    text-align: center !important;
    font-weight: 500 !important;
    padding: 8px 4px !important;
  }

  .competitor-table th.col-image,
  .competitor-table td.image-cell {
    width: 80px !important;
    min-width: 80px !important;
    max-width: 80px !important;
    text-align: center !important;
    padding: 5px !important;
  }

  .competitor-table th.col-name,
  .competitor-table td.name-cell {
    width: auto !important;
    min-width: 150px !important;
    padding: 8px 8px !important;
  }

  .competitor-table th.col-belt,
  .competitor-table td.belt-cell {
    width: 100px !important;
    min-width: 100px !important;
    max-width: 100px !important;
  }

  .competitor-table th.col-date,
  .competitor-table td.date-cell {
    width: 150px !important;
    min-width: 150px !important;
    max-width: 150px !important;
  }

  .competitor-table th.col-club,
  .competitor-table td.club-cell {
    width: auto !important;
    min-width: 200px !important;
    padding: 8px 8px !important;
  }

  .competitor-table th.col-placement,
  .competitor-table td.placement-cell {
    width: 80px !important;
    min-width: 80px !important;
    max-width: 80px !important;
    text-align: center !important;
    padding: 8px 4px !important;
  }

.competitor-table th.col-image,
.competitor-table td.image-cell {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  text-align: center;
  padding: 5px;
  vertical-align: middle;
  height: 80px;
  min-height: 80px;
}

.avatar-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 60px;
  height: 60px;
  vertical-align: middle;
}

.avatar-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  background-color: #f0f0f0;
  border: 1px solid #dcdfe6;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  flex-shrink: 0;
}

.competitor-avatar {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.competitor-table th.col-name,
.competitor-table td.name-cell {
  font-weight: 500;
  width: auto;
  min-width: 150px;
  padding: 8px 8px;
}

.competitor-table th.col-belt,
.competitor-table td.belt-cell {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
}

.competitor-table th.col-date,
.competitor-table td.date-cell {
  width: 150px;
  min-width: 150px;
  max-width: 150px;
}

.belt-cell,
.date-cell {
  color: #606266;
}

.competitor-table th.col-club,
.competitor-table td.club-cell {
  width: auto;
  min-width: 200px;
  max-width: none;
  box-sizing: border-box;
  padding: 8px 8px;
}

.competitor-table th.col-placement,
.competitor-table td.placement-cell {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  text-align: center;
  padding: 8px 4px;
}

.placement-checkbox {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 1px solid #333;
  background-color: #fff;
  vertical-align: middle;
}

.club-cell {
  vertical-align: middle;
  overflow: hidden;
  text-align: left;
}

.club-logo-wrapper {
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
}

.club-logo-placeholder {
  display: inline-block;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  background-color: #e8e8e8;
  border: 1px solid #d0d0d0;
  border-radius: 2px;
  vertical-align: middle;
  box-sizing: border-box;
}

.club-logo-placeholder::after {
  content: '';
  display: block;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E");
  background-size: 18px 18px;
  background-position: center;
  background-repeat: no-repeat;
}

.club-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  vertical-align: middle;
  display: inline-block;
}

.club-text {
  display: inline;
  vertical-align: middle;
  word-break: break-word;
}

.empty-message {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

@media print {
  .competitor-list-section {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    page-break-before: auto;
    page-break-inside: avoid;
    min-height: auto !important;
  }

  .competitor-table {
    display: table !important;
    visibility: visible !important;
    page-break-inside: auto;
    border-collapse: collapse !important;
    width: 100% !important;
    border-top: 1px solid #333 !important;
    border-bottom: 1px solid #333 !important;
    border-left: none !important;
    border-right: none !important;
    font-size: 12px !important;
  }

  .competitor-table thead {
    display: table-header-group !important;
    background-color: #f5f7fa !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .competitor-table tbody {
    display: table-row-group !important;
  }

  .competitor-table tr {
    display: table-row !important;
    page-break-inside: avoid;
    page-break-after: auto;
    height: auto !important;
    min-height: 80px !important;
  }

  .competitor-table th {
    border: none !important;
    border-bottom: 1px solid #333 !important;
    background-color: #f5f7fa !important;
    font-weight: 600 !important;
    padding: 10px 8px !important;
    color: #333 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .competitor-table td {
    border: none !important;
    border-bottom: 1px solid #333 !important;
    padding: 8px 8px !important;
    background-color: #fff !important;
    color: #333 !important;
    vertical-align: middle !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .competitor-table td.image-cell {
    height: 80px !important;
    min-height: 80px !important;
  }

  .avatar-container {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    position: relative !important;
    width: 60px !important;
    height: 60px !important;
    vertical-align: middle !important;
  }

  .avatar-placeholder {
    border-radius: 4px !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 60px !important;
    height: 60px !important;
    background-color: #f0f0f0 !important;
    border: 1px solid #dcdfe6 !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .competitor-avatar {
    border-radius: 4px !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 60px !important;
    height: 60px !important;
    object-fit: cover !important;
    position: relative !important;
    z-index: 1 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .image-cell {
    display: table-cell !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .club-logo {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 28px !important;
    height: 28px !important;
    object-fit: contain !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .club-logo-wrapper {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .club-logo-placeholder {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 28px !important;
    height: 28px !important;
    background-color: #e8e8e8 !important;
    border: 1px solid #d0d0d0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .club-cell {
    display: table-cell !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .placement-cell {
    display: table-cell !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .placement-checkbox {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 20px !important;
    height: 20px !important;
    border: 1px solid #333 !important;
    background-color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .empty-message {
    text-align: center;
    padding: 20px;
    color: #666;
    font-style: italic;
  }
}
</style>
